import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Scissors, Home, CheckCircle2, AlertCircle,
  PanelLeftClose, PanelLeftOpen, RotateCcw, Maximize2, Minimize2,
} from 'lucide-react';
import LotsSidebar from './planeditor/LotsSidebar';
import LotPickerCombobox from './planeditor/LotPickerCombobox';
import { FloorHspBadge } from './planeditor/FloorHspBadge';
import TabPreparation from './planeditor/TabPreparation';
import TabStructure from './planeditor/TabStructure';
import TabPieces from './planeditor/TabPieces';
import TabFurniture from './planeditor/TabFurniture';
import MaisonInitBanner from './planeditor/MaisonInitBanner';
import ResetPlanDialog from './planeditor/ResetPlanDialog';
import { PlanEditorProvider } from './planeditor/PlanEditorContext';
import LotStepRail from './planeditor/LotStepRail';
import { isMaisonProject } from '../../lib/projectMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PlanEditorV2Section — station de travail 3 colonnes (lots · canvas · options).
 *
 * V15.10 — Parcours simplifié en 3 étapes :
 *   1. Plan source — document + orientation/recadrage + mise à l'échelle.
 *   2. Structure — contours, murs, escaliers, éléments fixes, menuiseries.
 *   3. Pièces & Lancer — pièces typées puis maquette blanche déterministe.
 */
const PlanEditorV2Section = ({
  projectId, project, onSaved, onOpenProduction,
  activeTab = 'preparation', navSubstep = null, onNavigate,
  lotsPane = true,   // V17.1 — false : aucune liste de lots (Génération › Mobilier, lot choisi dans l'arbre)
  stepRail = false,  // V17.1 — rail vertical d'étapes à la place de la liste des lots (le lot est choisi dans l'arbre Maquette)
}) => {
  const isApartmentSingle = project?.project_type_code === 'apartment_single';
  const isMaison = isMaisonProject(project);
  const isMultiLot = !isApartmentSingle;

  // ----- Lots state (multi-lot projects only) -----
  const [lots, setLots] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  // Bumped after the Maison init endpoint creates floors/lots so the
  // useEffect below refetches the (now-populated) lot list.
  const [lotsRefreshKey, setLotsRefreshKey] = useState(0);
  const lastLotKey = `pe_last_lot_${projectId}`;
  const [selectedLotId, setSelectedLotIdState] = useState(() => {
    if (typeof window === 'undefined') return '';
    const fromUrl = new URLSearchParams(window.location.search).get('lot_id');
    if (fromUrl) return fromUrl;
    try { return localStorage.getItem(lastLotKey) || ''; } catch { return ''; }
  });
  const setSelectedLotId = useCallback((lid) => {
    setSelectedLotIdState(lid);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (lid) url.searchParams.set('lot_id', lid);
      else url.searchParams.delete('lot_id');
      window.history.replaceState({}, '', url.toString());
      try { if (lid) localStorage.setItem(lastLotKey, lid); } catch { /* no-op */ }
    }
  }, [lastLotKey]);
  const selectedLot = useMemo(
    () => lots.find((l) => l.lot_id === selectedLotId) || null,
    [lots, selectedLotId],
  );

  useEffect(() => {
    if (!isMultiLot || !projectId) return;
    let cancelled = false;
    setLoadingLots(true);
    axios.get(`${API}/projects/${projectId}/lots`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setLots(items);
        const known = selectedLotId && items.some((l) => l.lot_id === selectedLotId);
        if (!known && items.length) {
          // Dernier lot ouvert (localStorage) sinon le plus récemment modifié, sinon le premier.
          let stored = '';
          try { stored = localStorage.getItem(lastLotKey) || ''; } catch { /* no-op */ }
          const fallback = items.find((l) => l.lot_id === stored)
            || [...items].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))[0];
          setSelectedLotId(fallback.lot_id);
        }
      })
      .catch(() => { /* silent — legacy fallback */ })
      .finally(() => { if (!cancelled) setLoadingLots(false); });
    return () => { cancelled = true; };
  }, [isMultiLot, projectId, selectedLotId, setSelectedLotId, lotsRefreshKey, lastLotKey]);

  // ----- Per-lot saved state -----
  const projectLevelSaved = project?.general_info?.plan_source_editor || null;
  const [lotLevelSaved, setLotLevelSaved] = useState(null);
  const savedState = (isMultiLot && selectedLotId) ? lotLevelSaved : projectLevelSaved;

  const reloadSavedState = useCallback(() => {
    if (!isMultiLot || !projectId || !selectedLotId) {
      setLotLevelSaved(null);
      return;
    }
    axios.get(`${API}/projects/${projectId}/plan-source?lot_id=${selectedLotId}`,
      { withCredentials: true })
      .then((r) => setLotLevelSaved(r.data?.state || null))
      .catch(() => setLotLevelSaved(null));
  }, [isMultiLot, projectId, selectedLotId]);

  useEffect(() => { reloadSavedState(); }, [reloadSavedState]);

  // ----- Tabs -----
  // V14.68.22 — L'onglet actif (preparation/structure/pieces/validation) est
  // désormais piloté par la ligne 2 du ProjectHorizontalNav via les props
  // activeTab / navSubstep / onNavigate.

  // V14.62 Sprint 17 — User can fold the lots sidebar to reclaim ~250 px of
  // canvas width. The dropdown lot picker in the header always remains
  // available so the lot can be switched regardless of sidebar state.
  // Persisted in localStorage so the preference survives reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('peters.planEditorV2.sidebarCollapsed') === '1'; }
    catch { return false; }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('peters.planEditorV2.sidebarCollapsed', next ? '1' : '0'); }
      catch { /* noop */ }
      return next;
    });
  }, []);

  // ----- Status badges for the header -----
  const headerStatus = useMemo(() => {
    const planSet = !!savedState?.source_file_id;
    const polygon = (savedState?.polygon || []).length >= 3;
    const validated = !!savedState?.validated_at;
    return { planSet, polygon, validated };
  }, [savedState]);

  // V14.69 Phase 1 — Réinitialisation à la carte + remount du provider
  const [showReset, setShowReset] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // V14.82 — Mode plein écran (façon Homebyme) : l'éditeur recouvre toute la
  // fenêtre (menu projet, bandeaux GPS/Maison masqués). Échap pour sortir.
  // V14.83 — mémorisé par session (sessionStorage) + raccourci clavier F.
  const [fullscreen, setFullscreen] = useState(
    () => sessionStorage.getItem('planEditorFullscreen') === '1',
  );
  const toggleFullscreen = useCallback(() => {
    setFullscreen((f) => {
      const next = !f;
      sessionStorage.setItem('planEditorFullscreen', next ? '1' : '0');
      return next;
    });
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (e.key === 'Escape' && fullscreen) {
        // Défère : si un autre handler a consommé Échap (annulation d'un
        // tracé en cours, fermeture de dialog…), on reste en plein écran.
        setTimeout(() => {
          if (e.defaultPrevented) return;
          setFullscreen(false);
          sessionStorage.setItem('planEditorFullscreen', '0');
        }, 0);
      } else if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        toggleFullscreen();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen, toggleFullscreen]);

  return (
    <MaybeBodyPortal active={fullscreen}>
    <div className={fullscreen
           ? 'flex flex-col gap-3 fixed inset-0 z-[120] p-3 overflow-hidden'
           : 'flex flex-col h-[calc(100vh-150px)] min-h-[640px] gap-3'}
         style={fullscreen ? { background: '#0B0D11' } : undefined}
         data-testid="plan-editor-v2-root">
      {/* SPRINT 26 — Maison init banner. Self-hides when the project
          isn't a Maison or has already been seeded with floors. */}
      {isMaison && (
        <MaisonInitBanner
          project={{ ...project, id: projectId }}
          onSeeded={() => setLotsRefreshKey((k) => k + 1)}
        />
      )}

      {/* HEADER — barre compacte (V14.68.22 : titre fusionné dans le menu horizontal) */}
      <div className="flex items-center gap-2 px-3 h-10 rounded-lg border"
           style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'var(--c-border)' }}>
        <Scissors className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--c-gold)' }} />
        <span className="flex-1 min-w-0 text-[11px] text-gray-400 truncate">
          {selectedLot
            ? <>Lot : <span className="font-medium text-gray-200">{selectedLot.label || selectedLot.lot_id}</span></>
            : 'Sélectionnez un lot pour commencer.'}
        </span>
        {isMultiLot && (
          <LotPickerCombobox
            lots={lots}
            selectedLotId={selectedLotId}
            onSelect={setSelectedLotId}
            disabled={loadingLots}
          />
        )}
        {/* V14.77 — Réglage HSP toujours accessible (même sidebar repliée) */}
        <FloorHspBadge lot={selectedLot} onPatched={() => setLotsRefreshKey((k) => k + 1)} />
        <StatusChip ok={headerStatus.planSet} label="Plan source" />
        <StatusChip ok={headerStatus.polygon} label="Contour" />
        <StatusChip ok={headerStatus.validated} label="Validé" />
        {/* V14.82 — Plein écran */}
        <button
          type="button"
          onClick={toggleFullscreen}
          className={`inline-flex items-center gap-1 text-[10.5px] h-7 px-2 rounded border transition-colors ${fullscreen
            ? 'border-teal-400/50 text-teal-300 bg-teal-500/10'
            : 'border-white/10 text-gray-300 hover:bg-white/5'}`}
          title={fullscreen ? 'Quitter le plein écran (F ou Échap)' : 'Plein écran (F) — masque les menus pour maximiser le canvas'}
          data-testid="plan-editor-fullscreen-toggle"
          aria-pressed={fullscreen}
        >
          {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          <span className="hidden xl:inline">{fullscreen ? 'Réduire' : 'Plein écran'}</span>
        </button>
        <button
          type="button"
          onClick={() => setShowReset(true)}
          className="inline-flex items-center gap-1 text-[10.5px] h-7 px-2 rounded border border-rose-400/30 text-rose-300 hover:bg-rose-500/10"
          title="Réinitialiser les étapes du lot courant (à la carte)"
          data-testid="plan-editor-reset-btn"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span className="hidden xl:inline">Réinitialiser</span>
        </button>
        {isMultiLot && !stepRail && lotsPane && (
          <button
            type="button"
            onClick={toggleSidebar}
            className="inline-flex items-center gap-1 text-[10.5px] h-7 px-2 rounded border hover:bg-white/5"
            style={{ borderColor: 'var(--c-border)', color: 'var(--c-muted)' }}
            title={sidebarCollapsed ? 'Déplier la liste des lots' : 'Replier la liste des lots (gagner ~250 px de canvas)'}
            data-testid="plan-editor-sidebar-toggle"
            aria-pressed={sidebarCollapsed}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
            <span className="hidden xl:inline">{sidebarCollapsed ? 'Liste' : 'Plier'}</span>
          </button>
        )}
      </div>

      {/* MAIN LAYOUT — flex so sidebar can collapse to a thin rail */}
      <div className="flex-1 flex gap-3 min-h-0">
        {/* LEFT — V17.1 rail d'étapes · sinon liste des lots (full / collapsed / mono-lot) */}
        {!lotsPane && !stepRail ? null : stepRail ? (
          <LotStepRail step={activeTab} sub={navSubstep} onNavigate={onNavigate} savedState={savedState} projectId={projectId} lotId={selectedLotId} onImported={() => { setLotLevelSaved(null); setResetKey((k) => k + 1); reloadSavedState(); onNavigate?.('structure', 'contour'); }} />
        ) : isMultiLot ? (
          sidebarCollapsed ? (
            <div className="w-9 shrink-0 min-h-0 flex flex-col items-center rounded-lg border py-2 gap-2"
                 style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}
                 data-testid="plan-editor-lots-pane-collapsed">
              <button type="button" onClick={toggleSidebar}
                      className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/5"
                      style={{ color: 'var(--c-muted)' }}
                      title="Déplier la liste des lots"
                      data-testid="plan-editor-sidebar-expand-btn">
                <PanelLeftOpen className="w-4 h-4" />
              </button>
              <div className="flex-1 flex items-center justify-center">
                <span className="text-[9.5px] text-gray-500 select-none"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                  {lots.length} lot{lots.length > 1 ? 's' : ''} · Sélecteur en haut
                </span>
              </div>
            </div>
          ) : (
            <div className="w-[260px] xl:w-[300px] shrink-0 min-h-0 flex flex-col rounded-lg border"
                 style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}
                 data-testid="plan-editor-lots-pane">
              <LotsSidebar
                lots={lots}
                selectedLotId={selectedLotId}
                onSelect={setSelectedLotId}
                onSelectAndOpenStructure={(lotId) => {
                  setSelectedLotId(lotId);
                  onNavigate?.('structure', 'walls');
                }}
                loading={loadingLots}
                maisonMode={isMaison}
                projectId={projectId}
                onFloorPatched={() => setLotsRefreshKey((k) => k + 1)}
              />
            </div>
          )
        ) : (
          <div className="w-[200px] shrink-0 p-4 text-center text-[11px] text-gray-400 rounded-lg border"
               style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}>
            <Home className="w-5 h-5 mx-auto mb-2 opacity-50" />
            Projet mono-lot — pas de sélecteur nécessaire.
          </div>
        )}

        {/* CENTER + RIGHT — Tabs (takes all remaining width) */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          <PlanEditorProvider
            key={`${projectId}::${selectedLotId || 'project'}::r${resetKey}`}
            projectId={projectId}
            lotId={selectedLotId || null}
            initialStructure={savedState?.structure || null}
          >
            <div className="flex-1 min-h-0">
              {activeTab === 'preparation' && (
                <TabPreparation
                  projectId={projectId}
                  project={project}
                  selectedLot={selectedLot}
                  selectedLotId={selectedLotId}
                  isMultiLot={isMultiLot}
                  savedState={savedState}
                  onSavedStateChanged={reloadSavedState}
                  onProjectSaved={onSaved}
                  externalSub={navSubstep}
                />
              )}
              {activeTab === 'structure' && (
                <TabStructure
                  projectId={projectId}
                  selectedLot={selectedLot}
                  selectedLotId={selectedLotId}
                  savedState={savedState}
                  onSavedStateChanged={reloadSavedState}
                  externalSub={navSubstep}
                />
              )}
              {activeTab === 'furniture' && (
                <TabFurniture
                  projectId={projectId}
                  selectedLot={selectedLot}
                  selectedLotId={selectedLotId}
                  savedState={savedState}
                />
              )}
              {(activeTab === 'pieces' || activeTab === 'validation') && (
                <TabPieces
                  projectId={projectId}
                  selectedLot={selectedLot}
                  selectedLotId={selectedLotId}
                  savedState={savedState}
                  isMaison={isMaison}
                  onOpenProduction={onOpenProduction}
                />
              )}
            </div>
          </PlanEditorProvider>
        </div>
      </div>

      <ResetPlanDialog
        open={showReset}
        onOpenChange={setShowReset}
        projectId={projectId}
        lotId={selectedLotId || null}
        onDone={() => { reloadSavedState(); setResetKey((k) => k + 1); }}
      />
    </div>
    </MaybeBodyPortal>
  );
};

// V14.82 — rend les enfants dans document.body quand `active` (échappe aux
// ancêtres avec transform/backdrop-filter qui piègent position:fixed).
const MaybeBodyPortal = ({ active, children }) =>
  (active ? createPortal(children, document.body) : children);

const StatusChip = ({ ok, label }) => (
  <span
    title={label}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${ok
      ? 'border-teal-400/40 text-teal-300 bg-teal-500/10'
      : 'border-amber-400/40 text-amber-300 bg-amber-500/10'}`}
  >
    {ok ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
    <span className="hidden xl:inline">{label}</span>
  </span>
);

export default PlanEditorV2Section;
